/*
 * Swym Wishlist Plus - theme-side wiring.
 *
 * The Swym SDK itself arrives via the Shopify app embed
 * (shopify://apps/wishlist-plus/blocks/wishlist-app-embed/...), NOT from the
 * theme, and it publishes SwymPageData / SwymProductVariants / SwymProductInfo /
 * triggerSwymVariantEvent on its own. This file therefore does NOT bootstrap the
 * SDK - it only forwards variant changes to Swym's own hook so its hosted UI
 * stays in step with the selected variant.
 *
 * The header count renderer was removed alongside the count bubble (QA #102):
 * Figma draws no counter on the wishlist icon in any header state.
 *
 * Add-to-wishlist IS wired here, against Swym's v3 LIST API (fetchLists / createList /
 * addToList / deleteFromList). The controls are plain <button data-wishlist-button>
 * elements rendered by snippets/wishlist-button.liquid and deliberately do NOT carry
 * .swym-add-to-wishlist - the SDK claims any element with that class and restyles it
 * (40x40, border stripped, padding 0 20px) on its way to swapping in its own button,
 * which destroyed the Figma design. Binding by data attribute keeps the design AND
 * records real state; the controls used to be anchors to /pages/wishlist that
 * navigated away and saved nothing (R4 #153, 2.4).
 *
 * Why the LIST api and not addToWishList: `swat.addToWishList` / `removeFromWishList` /
 * `fetch` are Swym's v2 ANALYTICS methods. On the wire addToWishList POSTs only to
 * /api/v2/provider/collect - an event collector - and never writes a wishlist. `fetch`
 * reads those events back within the same session, so the heart looked saved and then
 * came back empty on reload, with `fetchLists` reporting zero lists throughout. The
 * durable store is /api/v3/lists/*. addToList fires Swym's own addedToWishlist JSEvent
 * internally, so its tracking and emails still fire without the v2 call.
 *
 * Two API details worth keeping: the SDK global is `_swat` (`window.swat` is never
 * defined), and a list item payload must NOT carry `stk` - the v3 schema rejects it with
 * a 400. Only empi/epi/du (+ optional dt/iu/pr) are accepted, and `du` must be absolute.
 *
 * Consent gate (Productive #225, "heart changes but nothing is saved"): Swym's app embed
 * (swym-relay-830) no longer loads the SDK unconditionally. Its loader asks Shopify's
 * Customer Privacy API first and, while the cookie banner is still undecided (or the
 * shopper declined), it logs "No customer consent to load Swym Wishlist Plus" and
 * `_swat` never exists. Every click then sat in the SwymCallbacks queue with the control
 * stuck on aria-busy - the half-opacity heart the client read as "the icon changes".
 * Swym's own buttons handle this with a consent popup whose markup this theme does not
 * render (the theme owns every trigger), so the click handler below takes over: a click
 * on the heart is the shopper explicitly asking for the wishlist, so while the banner is
 * undecided it grants the `preferences` category only (functional storage for the feature
 * they just requested, every other category left untouched - the same partial update
 * Swym's popup performs on Accept), which fires visitorConsentCollected, the embed loads
 * the SDK and the queued save completes. An explicit decline is respected: the click is
 * dropped and the control released rather than overriding the shopper's choice.
 *
 * No user-facing strings live here - all copy arrives via Liquid | t calls.
 */
(function () {
	"use strict";

	if (window.__swymWishlistInit) return;
	window.__swymWishlistInit = true;

	/* ---- PDP variant sync ----
	   global.js's VariantSelects.updateMasterId dispatches `variant:change` on
	   document with detail.variant. Swym's hosted UI reads the current variant
	   through its own hook, so forward the change to it. */

	document.addEventListener("variant:change", function (event) {
		var variant = event.detail && event.detail.variant;
		if (!variant || !variant.id) return;

		if (typeof window.triggerSwymVariantEvent === "function") {
			try {
				window.triggerSwymVariantEvent(variant.id);
			} catch (e) {
				/* Swym's own hook - never let it break variant selection. */
			}
		}
	});

	/* ---- Add / remove to wishlist ----
	   Swym hands the SDK over through the SwymCallbacks queue (it drains the queue
	   itself once ready, and pushing after that point still fires), so everything
	   that needs `swat` goes in there. */

	var SAVED = Object.create(null); /* variant id (string) -> true */

	/* ---- Guest wishlist carry-over (Productive #292) ----
	   Swym re-keys the shopper on login: the app embed publishes swymCustomerId, the
	   SDK posts /lists/user-validate-sync and swaps the guest regid for the account's.
	   On this store the lists written under the guest regid did not follow that swap,
	   so everything hearted before logging in vanished from the account (hearts hollow,
	   wishlist page empty - client QA 2026-09-15). The theme therefore keeps its own
	   copy of what a guest saves (the same v3 item payloads it wrote, mirrored from
	   Swym's list on every load) and, on the first theme page a logged-in customer
	   opens, writes any of them the account does not already hold into the account's
	   list, then drops the copy. Idempotent: items the account already has are skipped,
	   and a failed write keeps the copy for the next load. `customerId` comes from
	   window.theme (layout/theme.liquid) and is null for a guest. */
	var GUEST_KEY = "billini:wishlist:guest";
	var customerId = (window.theme && window.theme.customerId) || null;

	function readGuestItems() {
		try {
			return JSON.parse(window.localStorage.getItem(GUEST_KEY) || "{}") || {};
		} catch (e) {
			return {};
		}
	}

	function writeGuestItems(items) {
		try {
			if (Object.keys(items).length) window.localStorage.setItem(GUEST_KEY, JSON.stringify(items));
			else window.localStorage.removeItem(GUEST_KEY);
		} catch (e) {
			/* Storage blocked: the account keeps whatever Swym itself carried over. */
		}
	}

	function rememberGuestItem(item) {
		if (customerId || !item || !item.epi) return;
		var items = readGuestItems();
		items[String(item.epi)] = item;
		writeGuestItems(items);
	}

	function forgetGuestItem(epi) {
		if (customerId) return;
		var items = readGuestItems();
		delete items[String(epi)];
		writeGuestItems(items);
	}

	/* Swym list items carry the fields the theme writes (empi/epi/du/dt/iu/pr) plus
	   server-side extras the v3 write rejects, so copy only the accepted shape. */
	function guestItemFromList(item) {
		var copy = { empi: item.empi, epi: item.epi, du: item.du };
		if (item.dt) copy.dt = item.dt;
		if (item.iu) copy.iu = item.iu;
		if (item.pr !== undefined && item.pr !== null) copy.pr = item.pr;
		return copy;
	}

	function mergeGuestItems(swat) {
		var items = readGuestItems();
		var pending = Object.keys(items).filter(function (epi) {
			return !SAVED[epi] && items[epi] && items[epi].empi && items[epi].du;
		});
		/* Anything the account already holds (or that is malformed) is settled now;
		   only the writes below can keep an entry alive for a retry. */
		Object.keys(items).forEach(function (epi) {
			if (pending.indexOf(epi) === -1) delete items[epi];
		});
		if (!pending.length) {
			writeGuestItems({});
			return;
		}
		withList(
			swat,
			function (lid) {
				var remaining = pending.length;
				var added = 0;
				var finish = function () {
					writeGuestItems(items);
					reflectAll();
					/* Swym's wishlist page painted before these writes landed; one reload
					   shows the merged list. sessionStorage-guarded so it happens once. */
					if (!added || !document.getElementById("swym-wishlist-render-container")) return;
					try {
						if (window.sessionStorage.getItem(GUEST_KEY + ":reloaded")) return;
						window.sessionStorage.setItem(GUEST_KEY + ":reloaded", "1");
						window.location.reload();
					} catch (e) {
						/* No sessionStorage: leave the page as Swym painted it. */
					}
				};
				pending.forEach(function (epi) {
					var settled = false;
					var done = function (ok) {
						if (settled) return;
						settled = true;
						if (ok) {
							SAVED[epi] = true;
							delete items[epi];
							added++;
						}
						if (!--remaining) finish();
					};
					try {
						swat.addToList(
							lid,
							items[epi],
							function () {
								done(true);
							},
							function () {
								done(false);
							}
						);
					} catch (e) {
						done(false);
					}
				});
			},
			function () {
				/* No list to write to: keep the copy for the next load. */
			}
		);
	}

	function controls() {
		return document.querySelectorAll("[data-wishlist-button]");
	}

	function reflect(button) {
		var id = button.getAttribute("data-variant-id");
		button.setAttribute("aria-pressed", SAVED[id] ? "true" : "false");
	}

	function reflectAll() {
		var all = controls();
		for (var i = 0; i < all.length; i++) reflect(all[i]);
	}

	/* Public re-entry point. Saved state is hydrated ONCE when Swym's list fetch
	   resolves, so client-rendered cards injected after that (the Tagalys search drawer
	   and results grid) would show unfilled hearts for products the shopper has saved.
	   Those renderers call this after each injection. Reads the SAVED map already in
	   memory - no extra Swym request. */
	window.swymReflectAll = reflectAll;

	/* v3 list-item shape. `stk` is deliberately absent - including it makes the list
	   endpoint reject the whole write with a 400. */
	function payload(button) {
		return {
			empi: parseInt(button.getAttribute("data-product-id"), 10),
			epi: parseInt(button.getAttribute("data-variant-id"), 10),
			du: button.getAttribute("data-product-url"),
			dt: button.getAttribute("data-product-title"),
			iu: button.getAttribute("data-product-image"),
			pr: parseFloat(button.getAttribute("data-product-price")),
		};
	}

	function onSwym(callback) {
		window.SwymCallbacks = window.SwymCallbacks || [];
		window.SwymCallbacks.push(callback);
	}

	function sdkReady() {
		return !!(window._swat && typeof window._swat.addToList === "function");
	}

	/* ---- Consent gate (see header) ----
	   "granted"   - preferences allowed; the SDK is loaded or about to be.
	   "undecided" - banner still open, no choice recorded ('' from currentVisitorConsent).
	   "declined"  - the shopper said no; never override that here.
	   "unknown"   - no Customer Privacy API on the page; the embed's own fallback loads
	                 the SDK in that case, so treat it like granted. */
	function consentState() {
		var cp = window.Shopify && window.Shopify.customerPrivacy;
		if (!cp || typeof cp.preferencesProcessingAllowed !== "function") return "unknown";
		if (cp.preferencesProcessingAllowed()) return "granted";
		var current = (typeof cp.currentVisitorConsent === "function" && cp.currentVisitorConsent()) || {};
		var value = current.preferences;
		if (value === "no" || value === "false" || value === false) return "declined";
		return "undecided";
	}

	/* Grant the preferences category only. Prefer Swym's own manager (it preserves the
	   other categories and dispatches visitorConsentCollected, which is what the embed's
	   loader listens for); fall back to the Shopify API directly when the embed's consent
	   script has not arrived. */
	function grantPreferencesConsent(done) {
		var manager = window.SwymConsentManager;
		if (manager && typeof manager.grantConsent === "function") {
			try {
				Promise.resolve(manager.grantConsent()).then(
					function (ok) {
						done(!!ok);
					},
					function () {
						done(false);
					}
				);
				return;
			} catch (e) {
				/* fall through to the direct call */
			}
		}
		var cp = window.Shopify && window.Shopify.customerPrivacy;
		if (!cp || typeof cp.setTrackingConsent !== "function") return done(false);
		try {
			cp.setTrackingConsent({ preferences: true }, function (error) {
				if (error) return done(false);
				document.dispatchEvent(new CustomEvent("visitorConsentCollected"));
				done(true);
			});
		} catch (e) {
			done(false);
		}
	}

	/* The Customer Privacy API is loaded lazily (the embed calls Shopify.loadFeatures for
	   it), so a click in the first moments of the page can find it absent. Load it on demand
	   and settle within a few seconds either way - callers treat "still absent" as unknown. */
	function withConsentApi(done) {
		if (window.Shopify && window.Shopify.customerPrivacy) return done();
		var settled = false;
		var finish = function () {
			if (settled) return;
			settled = true;
			done();
		};
		if (window.Shopify && typeof window.Shopify.loadFeatures === "function") {
			try {
				window.Shopify.loadFeatures([{ name: "consent-tracking-api", version: "0.1" }], finish);
			} catch (e) {
				/* fall through to polling */
			}
		}
		var started = Date.now();
		(function poll() {
			if (settled) return;
			if ((window.Shopify && window.Shopify.customerPrivacy) || Date.now() - started > 4000) return finish();
			setTimeout(poll, 100);
		})();
	}

	/* ---- Wishlist page ----
	   Swym paints the saved-items list into #swym-wishlist-render-container on the page it
	   hosts (/pages/swym-wishlist, where the header icon points). With the SDK gated the
	   way the header comment describes, a shopper who has not answered the cookie banner
	   landed on a page that showed only its title - no list, no empty state. Arriving here
	   is the same explicit request for the feature as a heart click, so the same
	   preferences-only grant applies; a decline still leaves the page to Swym. */
	function unlockWishlistPage() {
		if (!document.getElementById("swym-wishlist-render-container")) return;
		if (sdkReady()) return;
		withConsentApi(function () {
			if (consentState() !== "undecided") return;
			grantPreferencesConsent(function () {
				/* The embed reloads the SDK on visitorConsentCollected; nothing else to do. */
			});
		});
	}
	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", unlockWishlistPage);
	} else {
		unlockWishlistPage();
	}

	/* ---- Guest opt-in popup (Productive #213) ----
	   After a guest saves a product, Swym's SDK opens its own "Subscribe To Interesting
	   Product Updates" modal a few seconds later (.swym-mailing-list-modal, driven by
	   the account's "check user preference" flag that the SDK fetches from Swym, not by
	   anything in the theme). The client read it as the heart doing the wrong thing.
	   The setting belongs off in the Swym admin; until it is, the modal is dismissed
	   the moment it renders through its own "No, Thanks" control, so Swym records the
	   decline exactly as if the shopper had clicked it and the save itself is untouched. */
	/* Client QA still met the modal after the first pass (2026-09-16). Two gaps:
	   Swym binds the "No, Thanks" handler after the modal paints, so a click fired
	   from the insertion record can land on an unbound control, and a modal Swym
	   keeps mounted and reveals by class alone never produces a childList record.
	   The dismissal now watches class/style changes as well, re-fires the decline
	   every 500ms for a few seconds while the modal is still there, and removes the
	   modal outright if the control never binds. component-wishlist.css keeps it from
	   painting in the meantime. */
	function optInModal() {
		return document.querySelector(".swym-mailing-list-modal");
	}
	var optInTimer = 0;
	var optInTries = 0;
	function dismissOptInModal() {
		optInTimer = 0;
		var modal = optInModal();
		if (!modal) {
			optInTries = 0;
			return;
		}
		var cancel = modal.querySelector(".swym-cancel-mailing-list-btn");
		if (cancel) cancel.click();
		if (optInTries++ < 6) {
			optInTimer = setTimeout(dismissOptInModal, 500);
			return;
		}
		optInTries = 0;
		if (modal.parentNode) modal.parentNode.removeChild(modal);
		if (document.body.style.overflow === "hidden") document.body.style.overflow = "";
	}
	var optInObserver = new MutationObserver(function () {
		if (!optInTimer && optInModal()) dismissOptInModal();
	});
	function watchOptInModal() {
		optInObserver.observe(document.body, {
			childList: true,
			subtree: true,
			attributes: true,
			attributeFilter: ["class", "style"],
		});
		if (optInModal()) dismissOptInModal();
	}
	if (document.body) watchOptInModal();
	else document.addEventListener("DOMContentLoaded", watchOptInModal);

	/* ---- Wishlist page titles ----
	   Productive #293. Swym prints product names as the catalogue stores them, ALL
	   CAPS; the design sets them in Title Case like every product card. The card
	   snippet downcases in Liquid and lets CSS `capitalize` restore Title Case.
	   Here the lowercase copy goes on a data attribute and component-wishlist.css
	   paints it through ::before, capitalised, over the (visually hidden) original.
	   Swym's list is a Vue app that keeps references to its own text nodes -
	   replacing the heading's text (an earlier version of this did) made Vue
	   re-insert its node beside the new one and the name rendered twice. Attributes
	   are safe: Vue leaves ones it does not manage alone, and if it ever recreates
	   the element the observer below simply sets the attribute again. */
	function titleCaseWishlistNames() {
		var container = document.getElementById("swym-wishlist-render-container");
		if (!container) return;
		var titles = container.querySelectorAll(".swym-title");
		for (var i = 0; i < titles.length; i++) {
			var lower = titles[i].textContent.trim().toLowerCase();
			if (titles[i].getAttribute("data-billini-title") !== lower) titles[i].setAttribute("data-billini-title", lower);
		}
	}

	/* ---- Wishlist page prices (Productive #293) ----
	   Swym prints its prices as <code><amount> ("AUD99.95"); the rest of the site uses
	   the shop's money format ("$99.95", theme.moneyFormat from layout/theme.liquid).
	   Same technique as the names: the formatted copy goes on data-billini-price and
	   component-wishlist.css paints it via ::before, Swym's own text node untouched
	   (rewriting it doubled up under Swym's re-render). Works for any 3-letter code, so
	   the US store's "USD" drops the same way. */
	function formatWishlistPrices() {
		var container = document.getElementById("swym-wishlist-render-container");
		if (!container) return;
		var format = (window.theme && window.theme.moneyFormat) || "${{amount}}";
		var values = container.querySelectorAll(".swym-product-price .swym-value");
		for (var i = 0; i < values.length; i++) {
			var match = values[i].textContent.trim().match(/^([A-Z]{3})\s*\$?\s*([\d.,]+)$/);
			if (!match) continue;
			var formatted = format.replace(/\{\{\s*amount[a-z_]*\s*\}\}/, match[2]).replace(/<[^>]*>/g, "").trim();
			if (values[i].getAttribute("data-billini-price") !== formatted) values[i].setAttribute("data-billini-price", formatted);
		}
	}

	/* ---- Wishlist page swatches ----
	   The design draws the card's colourway swatches under the name; Swym prints the
	   saved size instead and has no colour data. Each card's product is fetched once
	   (/products/<handle>.js) and its `x-sibling-handle:` tags - the same source
	   product-card-colour-swatches.liquid reads - become a row of shared colour tiles
	   (Shopify Files `colour-<key>.jpg`, the tile the PLP card shows). A tile that
	   does not exist drops out on error. Idempotent per card via a data attribute. */
	var wishlistProductCache = Object.create(null);

	/* Colour key for a sibling: its own `colour|<name>` tag (the merchandising colour
	   name, handleized - the key the Files tiles are named by). The card snippet reads
	   the same name from the siblings metafield, which the storefront product JSON
	   does not expose, so each sibling is fetched once (cached) to read its tag. A
	   product without the tag falls back to its handle minus the common style prefix. */
	function colourTagKey(product) {
		for (var t = 0; t < product.tags.length; t++) {
			if (product.tags[t].indexOf("colour|") === 0) return product.tags[t].slice(7).trim().toLowerCase().replace(/[\s_]+/g, "-");
		}
		return "";
	}

	function commonHandlePrefix(handles) {
		if (handles.length < 2) return "";
		var first = handles[0].split("-");
		var length = first.length;
		for (var i = 1; i < handles.length; i++) {
			var other = handles[i].split("-");
			var n = 0;
			while (n < length && n < other.length - 1 && other[n] === first[n]) n++;
			length = n;
		}
		return length ? first.slice(0, length).join("-") + "-" : "";
	}

	function handleColourKey(handle, prefix) {
		var key = prefix && handle.indexOf(prefix) === 0 ? handle.slice(prefix.length) : handle.split("-").slice(1).join("-");
		key = key.replace(/^extended-curve-/, "").replace(/^wide-fit-/, "").replace(/^curve-/, "");
		var segments = key.split("-");
		while (segments.length > 1 && /^\d+$/.test(segments[0])) segments.shift();
		return segments.join("-");
	}

	function fetchWishlistProduct(handle) {
		if (!wishlistProductCache[handle]) {
			wishlistProductCache[handle] = fetch("/products/" + handle + ".js", { credentials: "same-origin" }).then(function (response) {
				return response.ok ? response.json() : null;
			}).catch(function () {
				return null;
			});
		}
		return wishlistProductCache[handle];
	}

	function renderWishlistSwatches() {
		var container = document.getElementById("swym-wishlist-render-container");
		if (!container) return;
		var items = container.querySelectorAll(".swym-wishlist-item:not([data-billini-swatches])");
		for (var i = 0; i < items.length; i++) {
			(function (item) {
				var link = item.querySelector(".swym-wishlist-image-wrapper[href]");
				var match = link && /\/products\/([^/?#]+)/.exec(link.getAttribute("href"));
				if (!match) return;
				item.setAttribute("data-billini-swatches", "pending");
				var handle = match[1];
				fetchWishlistProduct(handle).then(function (product) {
					if (!product || !item.isConnected) return;
					var handles = [];
					for (var t = 0; t < product.tags.length; t++) {
						if (product.tags[t].indexOf("x-sibling-handle:") === 0) handles.push(product.tags[t].slice(17).trim());
					}
					if (!handles.length) handles = [product.handle];
					/* Productive #210: fetch past the cap of 6 so an offline sibling (skipped
					   below) gives its place to the next online one. */
					handles = handles.slice(0, 12);
					var prefix = commonHandlePrefix(handles);
					return Promise.all(handles.map(function (sibling) {
						return sibling === product.handle ? product : fetchWishlistProduct(sibling);
					})).then(function (siblings) {
						if (!item.isConnected) return;
						var seen = Object.create(null);
						var row = document.createElement("div");
						row.className = "swym-billini-swatches";
						row.setAttribute("role", "list");
						var shown = 0;
						for (var h = 0; h < handles.length && shown < 6; h++) {
							/* Productive #358: a sibling whose .js 404s is archived or not on the
							   Online Store - no swatch linking to a dead page. */
							if (!siblings[h]) continue;
							var key = colourTagKey(siblings[h]) || handleColourKey(handles[h], prefix);
							if (!key || seen[key]) continue;
							seen[key] = true;
							var swatch = document.createElement("a");
							swatch.href = "/products/" + handles[h];
							swatch.className = "swym-billini-swatch" + (handles[h] === product.handle ? " swym-billini-swatch--active" : "");
							swatch.setAttribute("role", "listitem");
							swatch.setAttribute("aria-label", key.replace(/-/g, " "));
							var img = document.createElement("img");
							img.src = "/cdn/shop/files/colour-" + key + ".jpg";
							img.alt = "";
							img.width = 14;
							img.height = 14;
							img.loading = "lazy";
							img.onerror = function () {
								var a = this.parentNode;
								if (a && a.parentNode) a.parentNode.removeChild(a);
							};
							swatch.appendChild(img);
							row.appendChild(swatch);
							shown++;
						}
						var anchor = item.querySelector(".swym-variant-title") || item.querySelector(".swym-product-price");
						if (anchor) item.insertBefore(row, anchor);
						else item.appendChild(row);
						item.setAttribute("data-billini-swatches", "done");
					});
				});
			})(items[i]);
		}
	}

	if (document.getElementById("swym-wishlist-render-container")) {
		var wishlistObserver = new MutationObserver(function () {
			titleCaseWishlistNames();
			formatWishlistPrices();
			renderWishlistSwatches();
		});
		wishlistObserver.observe(document.getElementById("swym-wishlist-render-container"), { childList: true, subtree: true, characterData: true });
		titleCaseWishlistNames();
		formatWishlistPrices();
		renderWishlistSwatches();
	}

	/* ---- Default list ----
	   Every write targets a list id. Swym does not create one until something is saved,
	   so resolve the shopper's first list and only create one when none exists. Requests
	   that arrive while that is in flight queue here rather than racing a second
	   createList and splitting saves across two lists. */

	var listId = null;
	var listPending = null;

	function withList(swat, done, fail) {
		if (listId) return done(listId);

		if (listPending) {
			listPending.push({ done: done, fail: fail });
			return;
		}
		listPending = [{ done: done, fail: fail }];

		var settle = function (id) {
			var queued = listPending || [];
			listPending = null;
			for (var i = 0; i < queued.length; i++) {
				if (id) queued[i].done(id);
				else queued[i].fail();
			}
		};

		swat.fetchLists({
			callbackFn: function (lists) {
				if (lists && lists.length && lists[0].lid) {
					listId = lists[0].lid;
					return settle(listId);
				}
				/* createList/addToList take positional args - an options object throws. */
				swat.createList(
					{ lname: "Wishlist" },
					function (list) {
						listId = list && list.lid;
						settle(listId);
					},
					function () {
						settle(null);
					}
				);
			},
			errorFn: function () {
				settle(null);
			},
		});
	}

	/* Hydrate saved state so a reload shows the same hearts filled. A guest's lists
	   are mirrored into the local copy at the same time; a customer's load writes back
	   anything that copy still holds (Productive #292, see mergeGuestItems). */
	onSwym(function (swat) {
		if (typeof swat.fetchLists !== "function") return;
		swat.fetchLists({
			callbackFn: function (lists) {
				var guestCopy = customerId ? null : {};
				if (lists && lists.length) {
					if (lists[0].lid) listId = lists[0].lid;
					for (var i = 0; i < lists.length; i++) {
						var items = lists[i].listcontents || [];
						for (var j = 0; j < items.length; j++) {
							if (!items[j] || !items[j].epi) continue;
							SAVED[String(items[j].epi)] = true;
							if (guestCopy) guestCopy[String(items[j].epi)] = guestItemFromList(items[j]);
						}
					}
				}
				if (guestCopy) writeGuestItems(guestCopy);
				else mergeGuestItems(swat);
				reflectAll();
			},
			errorFn: function () {
				/* Read failure just leaves every heart unfilled. */
			},
		});
	});

	document.addEventListener("click", function (event) {
		var button = event.target.closest && event.target.closest("[data-wishlist-button]");
		if (!button) return;

		event.preventDefault();
		if (button.getAttribute("aria-busy") === "true") return;

		var id = button.getAttribute("data-variant-id");
		if (!id) return;

		var removing = !!SAVED[id];
		button.setAttribute("aria-busy", "true");

		/* If the SDK never arrives (consent withheld after all, script blocked, offline)
		   the queued callback never runs - release the control so it is not inert for
		   the rest of the visit. Cleared as soon as the SDK picks the click up. */
		var release = function () {
			button.removeAttribute("aria-busy");
		};
		var busyTimer = setTimeout(release, 15000);

		var run = function () {
			onSwym(function (swat) {
				clearTimeout(busyTimer);
				save(swat);
			});
		};

		if (sdkReady()) return run();

		withConsentApi(function () {
			if (sdkReady()) return run();
			var state = consentState();
			if (state === "declined") {
				clearTimeout(busyTimer);
				return release();
			}
			if (state === "undecided") {
				grantPreferencesConsent(function (ok) {
					if (ok) return run();
					clearTimeout(busyTimer);
					release();
				});
				return;
			}
			run();
		});

		function save(swat) {
			var method = removing ? swat.deleteFromList : swat.addToList;
			if (typeof method !== "function" || typeof swat.fetchLists !== "function") {
				button.removeAttribute("aria-busy");
				return;
			}

			var settle = function (saved) {
				if (saved) SAVED[id] = true;
				else delete SAVED[id];
				/* Keep the guest copy in step with the write (#292); no-op for a customer. */
				if (saved) rememberGuestItem(payload(button));
				else forgetGuestItem(id);
				button.removeAttribute("aria-busy");
				reflectAll();
			};

			withList(
				swat,
				function (lid) {
					/* Swym validates the payload synchronously and throws rather than calling
					   the error callback, so the busy flag has to clear in a catch too -
					   otherwise a rejected payload leaves the control permanently inert. */
					try {
						method.call(
							swat,
							lid,
							payload(button),
							function () {
								settle(!removing);
							},
							function () {
								settle(removing);
							}
						);
					} catch (e) {
						settle(removing);
					}
				},
				function () {
					settle(removing);
				}
			);
		}
	});

	/* Variant switches repoint the PDP controls at the newly selected variant so the
	   save records the variant the shopper is actually looking at. */
	document.addEventListener("variant:change", function (event) {
		var variant = event.detail && event.detail.variant;
		if (!variant || !variant.id) return;

		var all = document.querySelectorAll(".product__media-overlay-wishlist[data-wishlist-button], .pdp-wishlist-button[data-wishlist-button]");
		for (var i = 0; i < all.length; i++) {
			all[i].setAttribute("data-variant-id", String(variant.id));
			reflect(all[i]);
		}
	});
})();
